import { Component, OnInit, Self, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StateService } from '@uirouter/angular';

import { forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import CONSTANTS from '@constants';
import { IFBBrandedRegistrationText } from '@interfaces';

import { BackgroundBrandedService } from '@app/shared/modules/background-branded/services/background-branded.service';
import { CompanyService } from '@app/shared/services/company.service';
import { fbEmailValidator } from '@app/modules/ui/validators/email-validator';
import { NgOnDestroy } from '@app/shared/services/ng-on-destroy.service';
import { RegistrationService } from '@app/modules/auth/containers/register/registration.service';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { featureFlags } from '@utils/feature-flags';

@Component({
  providers: [NgOnDestroy],
  selector: 'app-register',
  templateUrl: './register.component.html',
  standalone: false
})
export class RegisterComponent implements OnInit {
  noWelcomeTextFt = signal(featureFlags.isOn('Registration.NoWelcomeText'));
  isLoaded = false;
  isInProgress = false;

  allowedDomainsAsList = this.registrationService.allowedDomainsAsList;
  isDomainBlocked = this.registrationService.isDomainBlocked;

  companyName = this.companyService.data.name;
  cookiesLink = this.translateService.getLocalizedConstantString(CONSTANTS.WEB_LINKS.COOKIES);
  registrationLink = { domains: [''] };
  registrationPageBrandingTexts: IFBBrandedRegistrationText;

  invitationEmailForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, fbEmailValidator()])
  });

  constructor(
    @Self() private destroy: NgOnDestroy,
    private backgroundBrandedService: BackgroundBrandedService,
    private companyService: CompanyService,
    private registrationService: RegistrationService,
    private stateService: StateService,
    private translateService: TranslateService
  ) {}

  get emailControl() {
    return this.invitationEmailForm.controls.email;
  }

  ngOnInit(): void {
    forkJoin({
      branding: this.backgroundBrandedService.fetchBranding().pipe(catchError(_ => of(null))),
      registrationLink: this.registrationService.getRegistrationLinkOrRedirect(),
      company: this.registrationService.getCompanyOrRedirect()
    })
      .pipe(takeUntil(this.destroy))
      .subscribe(
        ({ registrationLink }) => {
          this.registrationLink = registrationLink;
          this.registrationPageBrandingTexts = this.registrationService.getLocalizedInterpolatedBrandingTexts(
            this.companyService.data.registrationPageBranding.details
          );
          this.isLoaded = true;
        },
        () => {
          this.isLoaded = true;
        }
      );
  }

  requestRegistrationEmail(): void {
    if (
      this.isInProgress ||
      this.isDomainBlocked(this.registrationLink.domains, this.emailControl.value) ||
      this.emailControl.invalid
    ) {
      return;
    }

    this.isInProgress = true;

    this.registrationService
      .requestRegistrationEmail(this.emailControl.value)
      .pipe(takeUntil(this.destroy))
      .subscribe(
        () => {
          this.stateService.go('register-email-sent', { email: this.emailControl.value });
        },
        () => {
          this.isInProgress = false;
        }
      );
  }
}
